import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { RaterRelationship } from '@genoma/shared';

import { useI18n } from '../../i18n/index.tsx';

/**
 * Where the assessments completed so far are coming from.
 *
 * A donut is legitimate here and not by default: part-to-whole read at a
 * glance, five slices, and a legend that carries the exact count and share —
 * so nothing depends on comparing arc lengths. It would be the wrong form for
 * comparing close values, which is what the legend is for.
 *
 * The colours are validated, not chosen: five steps of one teal ramp were the
 * obvious match for the mockup and fail outright — at the light end adjacent
 * steps land around ΔE 9, indistinguishable even with full colour vision. The
 * set used instead keeps the brand teal in the lead and clears the floors.
 */

/** Fixed order. A slice keeps its colour whatever the numbers do. */
const SERIES: { relationship: RaterRelationship; color: string }[] = [
  { relationship: 'SUPERVISOR', color: 'var(--color-series-1)' },
  { relationship: 'PEER', color: 'var(--color-series-2)' },
  { relationship: 'DIRECT_REPORT', color: 'var(--color-series-3)' },
  { relationship: 'STAKEHOLDER', color: 'var(--color-series-4)' },
  { relationship: 'SELF', color: 'var(--color-series-5)' },
];

type Props = {
  completed: Record<RaterRelationship, number>;
  invited: Record<RaterRelationship, number>;
};

/**
 * `completed` means finished and sent, not "has answered something". Fed the
 * looser number, opening a questionnaire and marking one screen showed up as
 * three evaluations received — and "recibidas" does not mean "begun".
 */
export const ParticipationDonut = ({ completed, invited }: Props) => {
  const { t } = useI18n();

  // The legend lists every relationship that was asked for, including those
  // with nothing back yet — a segment sitting at zero is precisely what P&O
  // needs to see. Only the ones with a value get an arc.
  const rows = SERIES.map((series) => ({
    ...series,
    label: t(`relationships.${series.relationship}`),
    value: completed[series.relationship] ?? 0,
    expected: invited[series.relationship] ?? 0,
  })).filter((row) => row.expected > 0);

  const slices = rows.filter((row) => row.value > 0);

  const received = rows.reduce((total, row) => total + row.value, 0);
  const expected = rows.reduce((total, row) => total + row.expected, 0);
  const pending = Math.max(expected - received, 0);
  const share = (value: number) => (received === 0 ? 0 : Math.round((value / received) * 100));

  return (
    <section className="rounded-2xl border border-line bg-paper px-5 py-4 shadow-card">
      <h2 className="flex items-center gap-2.5 text-[15px] font-bold">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden
          className="size-[18px] text-teal"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
        {t('dashboard.participationTitle')}
      </h2>

      <div className="mt-4 flex flex-wrap items-center justify-center gap-5">
        <div className="relative size-[150px] shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={slices}
                dataKey="value"
                nameKey="label"
                innerRadius="62%"
                outerRadius="100%"
                startAngle={90}
                endAngle={-270}
                // A 2px gap in the surface colour separates touching slices —
                // white doing the separating, rather than a stroke that would
                // add ink which is not data.
                paddingAngle={1.4}
                stroke="var(--color-paper)"
                strokeWidth={2}
                isAnimationActive={false}
              >
                {slices.map((slice) => (
                  <Cell key={slice.relationship} fill={slice.color} />
                ))}
              </Pie>
              <Tooltip
                cursor={false}
                formatter={(value, name) => {
                  const count = typeof value === 'number' ? value : 0;
                  return [`${count} · ${share(count)}%`, String(name)];
                }}
                contentStyle={{
                  borderRadius: 12,
                  border: '1px solid var(--color-line)',
                  fontSize: 12.5,
                  boxShadow: 'var(--shadow-card)',
                }}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* The hero figure sits in the hole rather than as a slice label. */}
          <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
            <div>
              <p className="text-[30px] leading-none font-extrabold tracking-[-0.03em] tabular-nums">
                {received}
              </p>
              <p className="mx-auto mt-1 max-w-[76px] text-[10.5px] leading-[1.2] text-soft">
                {t('dashboard.responsesReceived')}
              </p>
            </div>
          </div>
        </div>

        {/* The legend is the dependable identity channel: colour never carries
            it alone, and the exact numbers live here rather than on the arcs. */}
        <div className="min-w-[210px] flex-1">
          {/* Two different questions sit side by side — "is this segment done?"
              and "how much of the whole is it?" — so each column says which. */}
          <div className="flex items-center gap-2.5 border-b border-line-soft pb-1">
            <span aria-hidden className="size-2.5 shrink-0" />
            <span className="flex-1" />
            <span className="w-14 text-right font-mono text-[8.5px] tracking-[0.08em] whitespace-nowrap text-faint uppercase">
              {t('dashboard.legendDone')}
            </span>
            <span className="w-12 text-right font-mono text-[8.5px] tracking-[0.08em] whitespace-nowrap text-faint uppercase">
              {t('dashboard.legendShare')}
            </span>
          </div>

          <ul className="list-none">
            {rows.map((row) => (
              <li
                key={row.relationship}
                className="flex items-center gap-2.5 border-t border-line-soft/60 py-1.5 first:border-t-0"
              >
                <span
                  aria-hidden
                  className="size-2.5 shrink-0 rounded-[3px]"
                  style={{ background: row.color }}
                />
                <span className="flex-1 text-[13.5px]">{row.label}</span>
                <span
                  className={[
                    'w-14 text-right font-mono text-[13px] font-bold tabular-nums',
                    row.value >= row.expected ? 'text-teal-deep' : 'text-ink',
                  ].join(' ')}
                >
                  {row.value}/{row.expected}
                </span>
                <span className="w-12 text-right font-mono text-[12.5px] tabular-nums text-faint">
                  {share(row.value)}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-4 flex items-end justify-between border-t border-line-soft pt-3.5">
        <div>
          <p className="text-[12.5px] text-soft">{t('dashboard.pending')}</p>
          <p className="text-[26px] leading-none font-extrabold tracking-[-0.03em] text-teal tabular-nums">
            {pending}
          </p>
          <p className="mt-0.5 text-[12px] text-soft">{t('dashboard.pendingUnit')}</p>
        </div>
        <div className="text-right">
          <p className="text-[12.5px] text-soft">{t('dashboard.totalAssessments')}</p>
          <p className="text-[26px] leading-none font-extrabold tracking-[-0.03em] tabular-nums">
            {expected}
          </p>
        </div>
      </div>
    </section>
  );
};
